import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaPlus, FaTrash, FaBuilding, FaUser, FaPercent, FaEuroSign, FaExclamationTriangle } from 'react-icons/fa';

const OrderSplit = ({ 
  activityKey,
  patientData,
  availableCompanies = [],
  isLoadingCompanies = false,
  onSplitChange,
  initialSplits = null,
  className = "" 
}) => {
  const [splits, setSplits] = useState([]);
  const [initialized, setInitialized] = useState(false);
  
  // Initialize splits only once
  useEffect(() => {
    if (!initialized) {
      console.log(`OrderSplit: Inicializando splits para ${activityKey}`);
      console.log('Initial splits:', initialSplits);
      console.log('Patient data:', patientData);
      
      if (initialSplits && Array.isArray(initialSplits) && initialSplits.length > 0) {
        console.log('Usando splits iniciales proporcionados');
        setSplits(initialSplits);
      } else {
        console.log('Creando split por defecto del paciente');
        const defaultSplit = {
          id: Date.now(),
          payer_id: patientData?.patientId || '',
          payer_type: 'User',
          percent: 100,
          max_amount: null
        };
        setSplits([defaultSplit]);
      }
      setInitialized(true);
    }
  }, [activityKey, patientData, initialSplits, initialized]);

  // Memoized validation calculation
  const validation = useMemo(() => {
    const total = splits.reduce((sum, split) => sum + (parseFloat(split.percent) || 0), 0);
    const errors = [];
    
    // Validate percentages (más tolerante para números decimales)
    const percentValid = Math.abs(total - 100) <= 0.1;
    if (!percentValid) {
      errors.push(`Los porcentajes suman ${total.toFixed(2)}% (debe ser 100%)`);
    }
    
    // Validate all splits have valid payer_id
    const emptySplits = splits.filter(split => !split.payer_id || split.payer_id.toString().trim() === '');
    if (emptySplits.length > 0) {
      errors.push(`${emptySplits.length} pagador(es) sin seleccionar`);
    }
    
    // Validate no negative percentages
    const invalidPercents = splits.filter(split => split.percent < 0 || split.percent > 100);
    if (invalidPercents.length > 0) {
      errors.push(`Porcentajes inválidos detectados`);
    }
    
    // Validate at least one split exists
    if (splits.length === 0) {
      errors.push('Debe tener al menos un pagador');
    }
    
    return {
      totalPercent: total,
      errors,
      isValid: errors.length === 0
    };
  }, [splits]);

  // Stable callback for notifying parent
  const notifyParent = useCallback((currentSplits, isValid) => {
    if (onSplitChange && initialized) {
      console.log(`OrderSplit: Notificando cambios para ${activityKey}`);
      console.log('Splits actuales:', currentSplits);
      console.log('Es válido:', isValid);
      onSplitChange(activityKey, currentSplits, isValid);
    }
  }, [onSplitChange, activityKey, initialized]);

  // Notify changes to parent component when splits or validation changes
  useEffect(() => {
    if (initialized) {
      notifyParent(splits, validation.isValid);
    }
  }, [splits, validation.isValid, notifyParent, initialized]);

  // Add new split
  const addSplit = useCallback(() => {
    console.log('Añadiendo nuevo split');
    
    setSplits(currentSplits => {
      const newSplits = [...currentSplits];
      
      // Redistribute percentages proportionally
      if (validation.totalPercent >= 100 && newSplits.length > 0) {
        const reductionPerSplit = 100 / (newSplits.length + 1);
        
        newSplits.forEach((split, index) => {
          newSplits[index] = { ...split, percent: Math.round(reductionPerSplit * 100) / 100 };
        });
      }
      
      const totalAfterReduction = newSplits.reduce((sum, s) => sum + s.percent, 0);
      const remainingPercent = Math.max(0, 100 - totalAfterReduction);
      
      const newSplit = {
        id: Date.now(),
        payer_id: '',
        payer_type: 'Company',
        percent: remainingPercent > 0 ? Math.round(remainingPercent * 100) / 100 : Math.round((100 / (newSplits.length + 1)) * 100) / 100,
        max_amount: null
      };
      
      const finalSplits = [...newSplits, newSplit];
      console.log('Splits después de añadir:', finalSplits);
      
      return finalSplits;
    });
  }, [validation.totalPercent]);

  // Update specific split
  const updateSplit = useCallback((index, field, value) => {
    console.log(`Actualizando split ${index}, campo: ${field}, valor:`, value);
    
    setSplits(currentSplits => {
      const newSplits = [...currentSplits];
      newSplits[index] = { ...newSplits[index], [field]: value };
      
      // If payer type changes, clear payer_id
      if (field === 'payer_type') {
        newSplits[index].payer_id = '';
      }
      
      console.log('Splits después de actualizar:', newSplits);
      return newSplits;
    });
  }, []);

  // Update percentage with validation
  const handlePercentChange = useCallback((index, value) => {
    const percent = Math.max(0, Math.min(100, parseFloat(value) || 0));
    updateSplit(index, 'percent', percent);
  }, [updateSplit]);

  // Remove split
  const removeSplit = useCallback((index) => {
    setSplits(currentSplits => {
      if (currentSplits.length > 1) {
        const splitToRemove = currentSplits[index];
        
        // Don't allow removing patient split if it's the only User type
        const userSplits = currentSplits.filter(s => s.payer_type === 'User');
        if (splitToRemove.payer_type === 'User' && userSplits.length === 1) {
          alert('Debe mantener al menos un split del paciente');
          return currentSplits;
        }
        
        return currentSplits.filter((_, i) => i !== index);
      }
      return currentSplits;
    });
  }, []);

  // Distribute percentages evenly
  const distributeEvenly = useCallback(() => {
    setSplits(currentSplits => {
      const percentPerSplit = Math.round((100 / currentSplits.length) * 100) / 100;
      let remainder = 100 - (percentPerSplit * currentSplits.length);
      
      return currentSplits.map((split, index) => ({
        ...split,
        percent: index === 0 ? percentPerSplit + remainder : percentPerSplit
      }));
    });
  }, []);

  // Get payer options by type
  const getPayerOptions = useCallback((currentPayerType) => {
    if (currentPayerType === 'User') {
      // For User type, only return the patient
      return [{
        id: patientData?.patientId,
        type: 'User',
        name: getPatientFullName(patientData),
        display_name: getPatientFullName(patientData)
      }];
    }
    
    return availableCompanies.filter(company => 
      company.type === currentPayerType ||
      (currentPayerType === 'Company' && company.type === 'Company') ||
      (currentPayerType === 'HealthCenter' && company.type === 'HealthCenter')
    );
  }, [availableCompanies, patientData]);

  // Get patient full name
  const getPatientFullName = useCallback((patient) => {
    if (!patient) return 'Paciente Desconocido';
    const firstName = patient.patientName || patient.name || '';
    const lastName = patient.surname || '';
    return `${firstName} ${lastName}`.trim() || 'Sin nombre';
  }, []);

  // Get payer name
  const getPayerName = useCallback((payerId, payerType) => {
    if (!payerId) return 'Seleccionar...';
    
    if (payerType === 'User' && payerId === patientData?.patientId) {
      return getPatientFullName(patientData);
    }
    
    const payer = availableCompanies.find(p => p.id === payerId);
    if (payer) {
      return payer.name || payer.display_name || payer.full_name;
    }
    
    return 'Desconocido';
  }, [patientData, availableCompanies, getPatientFullName]);

  // Calculate amount for a given percentage
  const calculateAmount = useCallback((percent, totalAmount = 100) => {
    return (totalAmount * percent / 100).toFixed(2);
  }, []);

  // Validate individual split
  const validateSplit = useCallback((split, index) => {
    const errors = [];
    
    if (!split.payer_id || split.payer_id.toString().trim() === '') {
      errors.push('Debe seleccionar un pagador');
    }
    
    if (split.percent < 0) {
      errors.push('El porcentaje no puede ser negativo');
    }
    
    if (split.percent > 100) {
      errors.push('El porcentaje no puede exceder 100%');
    }
    
    return errors;
  }, []);

  // Don't render until initialized
  if (!initialized) {
    return <div className="text-center">Inicializando...</div>;
  }

  return (
    <div className={`order-split ${className}`}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-1">
            <FaPercent className="me-2" />
            Configuración de Pagadores
          </h6>
          <small className="text-muted">
            Actividad: {activityKey}
          </small>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={distributeEvenly}
            title="Distribuir uniformemente"
            disabled={splits.length === 0}
          >
            Distribuir Uniformemente
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={addSplit}
            title="Agregar Pagador"
            disabled={splits.length >= 5}
          >
            <FaPlus className="me-1" />
            Agregar Pagador
          </button>
        </div>
      </div>

      {/* Splits list */}
      <div className="splits-list">
        {splits.map((split, index) => {
          const splitErrors = validateSplit(split, index);
          const isPatientSplit = split.payer_id === patientData?.patientId && split.payer_type === 'User';
          const hasErrors = splitErrors.length > 0;
          
          return (
            <div key={split.id || index} className={`card mb-2 ${hasErrors ? 'border-warning' : ''}`}>
              <div className="card-body p-3">
                <div className="row g-2 align-items-center">
                  {/* Payer type */}
                  <div className="col-md-3">
                    <label className="form-label small">Tipo</label>
                    <select
                      className="form-select form-select-sm"
                      value={split.payer_type}
                      onChange={(e) => updateSplit(index, 'payer_type', e.target.value)}
                      disabled={isPatientSplit}
                    >
                      <option value="User">Paciente</option>
                      <option value="Company">Aseguradora/Empresa</option>
                      <option value="HealthCenter">Centro Médico</option>
                    </select>
                  </div>

                  {/* Payer selection */}
                  <div className="col-md-3">
                    <label className="form-label small">Pagador</label>
                    {split.payer_type === 'User' ? (
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        value={getPatientFullName(patientData)}
                        readOnly
                      />
                    ) : (
                      <select
                        className={`form-select form-select-sm ${!split.payer_id ? 'border-warning' : ''}`}
                        value={split.payer_id}
                        onChange={(e) => updateSplit(index, 'payer_id', e.target.value)}
                        disabled={isLoadingCompanies}
                      >
                        <option value="">
                          {isLoadingCompanies ? 'Cargando...' : '-- Seleccionar pagador --'}
                        </option>
                        {getPayerOptions(split.payer_type).map(payer => (
                          <option key={payer.id} value={payer.id}>
                            {payer.name || payer.display_name}
                            {payer.cif && ` (${payer.cif})`}
                          </option>
                        ))}
                      </select>
                    )}
                    
                    {/* Show split errors */}
                    {splitErrors.length > 0 && (
                      <div className="text-warning small mt-1">
                        {splitErrors.map((error, i) => (
                          <div key={i}>
                            <FaExclamationTriangle className="me-1" />
                            {error}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Percentage */}
                  <div className="col-md-2">
                    <label className="form-label small">% Copago</label>
                    <div className="input-group input-group-sm">
                      <input
                        type="number"
                        className={`form-control ${split.percent <= 0 || split.percent > 100 ? 'border-warning' : ''}`}
                        value={split.percent}
                        onChange={(e) => handlePercentChange(index, e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                      />
                      <span className="input-group-text">%</span>
                    </div>
                  </div>

                  {/* Calculated amount (placeholder - will be calculated with real total) */}
                  <div className="col-md-2">
                    <label className="form-label small">Importe*</label>
                    <div className="form-control form-control-sm bg-light text-end">
                      €{calculateAmount(split.percent)}
                    </div>
                    <small className="text-muted">*Estimado</small>
                  </div>

                  {/* Maximum cap (optional) */}
                  <div className="col-md-1">
                    <label className="form-label small">Tope €</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={split.max_amount || ''}
                      onChange={(e) => updateSplit(index, 'max_amount', e.target.value ? parseFloat(e.target.value) : null)}
                      min="0"
                      step="0.01"
                      placeholder="Sin tope"
                    />
                  </div>

                  {/* Actions */}
                  <div className="col-md-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeSplit(index)}
                      disabled={splits.length <= 1 || (isPatientSplit && splits.filter(s => s.payer_type === 'User').length === 1)}
                      title={isPatientSplit ? 'No se puede eliminar el único split del paciente' : 'Eliminar pagador'}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                {/* Additional split information */}
                {split.max_amount && parseFloat(calculateAmount(split.percent)) > split.max_amount && (
                  <div className="alert alert-info alert-sm mt-2 mb-0">
                    <strong>Tope aplicado:</strong> €{split.max_amount} 
                    (calculado: €{calculateAmount(split.percent)})
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary and total validation */}
      <div className="validation-summary">
        <div className={`card ${validation.isValid ? 'border-success' : 'border-danger'}`}>
          <div className="card-body p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong className={validation.isValid ? 'text-success' : 'text-danger'}>
                  Total porcentajes: {validation.totalPercent.toFixed(2)}%
                </strong>
                
                {/* Show validation errors */}
                {validation.errors.length > 0 && (
                  <div className="text-danger small mt-1">
                    {validation.errors.map((error, index) => (
                      <div key={index}>
                        <FaExclamationTriangle className="me-1" />
                        {error}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Validation status */}
                <div className="small mt-1">
                  {validation.isValid ? (
                    <span className="text-success">
                      ✓ Configuración válida
                    </span>
                  ) : (
                    <span className="text-danger">
                      ✗ Configuración incompleta
                    </span>
                  )}
                </div>
              </div>
              
              <div className="text-end">
                <div className="text-muted small">Distribución estimada:</div>
                
                {/* Distribution by payer */}
                {validation.isValid && (
                  <div className="mt-1">
                    {splits.filter(split => split.payer_id).map((split, idx) => (
                      <div key={split.id || idx} className="text-xs text-muted">
                        {getPayerName(split.payer_id, split.payer_type)}: 
                        {split.percent.toFixed(2)}%
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Debug info (siempre visible para diagnosticar) */}
      <div className="mt-2">
        <small className="text-muted">
          Debug - Activity Key: {activityKey} | Valid: {validation.isValid ? 'Yes' : 'No'} | 
          Splits: {splits.length} | Total%: {validation.totalPercent.toFixed(2)} | 
          Errors: {validation.errors.join(', ') || 'None'}
        </small>
      </div>
    </div>
  );
};

export default OrderSplit;